import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ProgressBar from '../../../components/ui/ProgressBar';
import { dailyTaskService } from '../../../services/taskService';
import { siteService } from '../../../services/siteService';
import { workerService } from '../../../services/workerService';

const TaskRollupCard = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('sites');
  const [tasks, setTasks] = useState([]);
  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksResult, sitesResult, workersResult] = await Promise.all([
        dailyTaskService?.getForDateRange(sevenDaysAgo, today, null),
        siteService?.getAllSites(),
        workerService?.getAllWorkers(),
      ]);
      setTasks(tasksResult?.data || []);
      setSites(sitesResult?.data || []);
      setWorkers((workersResult?.data || [])?.filter(w => w?.role === 'worker'));
    } catch (e) {
      // non-critical
    } finally {
      setLoading(false);
    }
  };

  const totalCompleted = tasks?.filter(t => t?.status === 'completed')?.length;
  const totalTasks = tasks?.length;
  const overallRate = totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
  const overdueCount = dailyTaskService?.getOverdueTasks(tasks)?.length;

  const siteStats = dailyTaskService?.groupBySite(tasks, sites)?.filter(s => s?.tasks_total > 0);
  const workerStats = dailyTaskService?.groupByWorker(tasks, workers);
  const getBarColor = (rate) => rate >= 80 ? 'bg-emerald-500' : rate >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const sitesNeedingAttention = siteStats?.filter(s => s?.completion_rate < 50 || s?.tasks_overdue > 0);

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-2 bg-muted rounded" />
          <div className="h-16 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Icon name="BarChart2" size={18} className="text-accent" /><div><h3 className="font-semibold text-foreground font-heading">Daily Tasks</h3><p className="text-xs text-muted-foreground">Last 7 days</p></div></div>
          <button onClick={() => navigate('/task-planner')} className="flex items-center gap-1.5 text-xs font-semibold text-accent hover:text-accent/80 transition-colors"><Icon name="Plus" size={13} />Plan Tasks</button>
        </div>
        <div className="grid grid-cols-4 gap-3 mb-3">
          {[
            { label: 'Total', value: totalTasks, color: 'text-foreground' },
            { label: 'Done', value: totalCompleted, color: 'text-emerald-600' },
            { label: 'Overdue', value: overdueCount, color: 'text-orange-600' },
            { label: 'Rate', value: `${overallRate}%`, color: overallRate >= 70 ? 'text-emerald-600' : 'text-amber-600' },
          ]?.map(stat => (
            <div key={stat?.label} className="text-center">
              <p className={`text-xl font-bold ${stat?.color}`}>{stat?.value}</p>
              <p className="text-xs text-muted-foreground">{stat?.label}</p>
            </div>
          ))}
        </div>
        <ProgressBar value={overallRate} color={getBarColor(overallRate)} height="h-2" showValue={false} label="Overall Progress" />
      </div>
      <div className="flex border-b border-border">
        {[{ id: 'sites', label: 'By Site' }, { id: 'workers', label: 'Workers' }]?.map(tab => (
          <button key={tab?.id} onClick={() => setView(tab?.id)} className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${view === tab?.id ? 'text-accent border-b-2 border-accent' : 'text-muted-foreground hover:text-foreground'}`}>{tab?.label}</button>
        ))}
      </div>
      <div className="divide-y divide-border">
        {view === 'sites' && siteStats?.map(site => (
          <div key={site?.id} className="px-5 py-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => navigate(`/task-completion-workflow?site=${site?.id}`)}>
            <div className="flex items-center justify-between mb-1.5">
              <div><p className="text-sm font-medium text-foreground">{site?.site_name}</p><p className="text-xs text-muted-foreground">{site?.tasks_completed}/{site?.tasks_total} tasks</p></div>
              <div className="text-right">
                <p className={`text-sm font-bold ${site?.completion_rate >= 80 ? 'text-emerald-600' : site?.completion_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{site?.completion_rate}%</p>
                {site?.tasks_overdue > 0 && <p className="text-xs text-orange-600 font-medium">{site?.tasks_overdue} overdue</p>}
              </div>
            </div>
            <ProgressBar value={site?.completion_rate} color={getBarColor(site?.completion_rate)} showValue={false} label={`${site?.site_name} Progress`} />
          </div>
        ))}
        {view === 'sites' && siteStats?.length === 0 && (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">No tasks in the last 7 days</div>
        )}
        {view === 'workers' && workerStats?.map(worker => (
          <div key={worker?.id} className="px-5 py-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => navigate(`/task-completion-workflow?worker=${worker?.id}`)}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-accent">{worker?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)}</span></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-foreground truncate">{worker?.full_name}</p>
                  <span className={`text-xs font-bold ml-2 ${worker?.completion_rate >= 80 ? 'text-emerald-600' : worker?.completion_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{worker?.completion_rate}%</span>
                </div>
                <ProgressBar value={worker?.completion_rate} color={getBarColor(worker?.completion_rate)} showValue={false} label={`${worker?.full_name} Progress`} />
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-muted-foreground">{worker?.tasks_completed}/{worker?.tasks_total}</span>
                  {worker?.tasks_overdue > 0 && <span className="text-xs text-orange-600 font-medium">{worker?.tasks_overdue} overdue</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
        {view === 'workers' && workerStats?.length === 0 && (
          <div className="px-5 py-6 text-center text-sm text-muted-foreground">No worker tasks in the last 7 days</div>
        )}
      </div>
      {sitesNeedingAttention?.length > 0 && (
        <div className="px-5 py-3 bg-orange-50 border-t border-orange-100">
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={14} className="text-orange-600" />
            <p className="text-xs font-semibold text-orange-700">{sitesNeedingAttention?.length} site{sitesNeedingAttention?.length !== 1 ? 's' : ''} need{sitesNeedingAttention?.length === 1 ? 's' : ''} attention</p>
            <button onClick={() => navigate('/task-completion-workflow')} className="ml-auto text-xs font-semibold text-orange-700 hover:underline">View →</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskRollupCard;
